import { useMemo } from "react";
import { Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  coerceLocale,
  catalogBySlug as buildCatalogBySlug,
  muscleName,
  planMuscles,
  type Exercise,
  type PlannedExerciseEntry,
  type UnitsPreference,
} from "@jackedlogs/shared";

import type { UiTheme } from "../theme";
import { makePreviewStyles } from "../Programs/previewStyles";
import { PlanIntensityMatrix } from "./PlanIntensityMatrix";

// Read-only plan preview (inline-expanded under a PlanRow). A plan is a
// single day, so instead of the program's session × week heat grid it shows
// an exercise × set intensity matrix (the workout's shape at a glance) + an
// on-demand per-exercise detail — the SAME UX language as ProgramPreview,
// applied to a single session. Authoring stays web-only. Name resolution is
// host-provided (resolveName) so the same tree renders on mobile and the
// web-rnw console.
export function PlanPreview({
  entries,
  catalog,
  resolveName,
  unit = "kg",
  theme,
}: {
  entries: PlannedExerciseEntry[];
  catalog: Exercise[] | null;
  resolveName: (slug: string, snapshot?: string) => string;
  // Display unit for prescribed loads (stored kg). Default kg = unchanged.
  unit?: UnitsPreference;
  theme: UiTheme;
}) {
  const { t, i18n } = useTranslation();
  const lang = coerceLocale(i18n.language);
  const styles = makePreviewStyles(theme);

  const catalogBySlug = useMemo(
    () => buildCatalogBySlug(catalog ?? []),
    [catalog],
  );

  // Derivation shared with ProgramPreview + the Library tag-filter
  // (@jackedlogs/shared muscleDerivation); sort-by-localized-name stays here.
  const muscles = useMemo(
    () =>
      planMuscles(entries, catalogBySlug).sort((a, b) =>
        muscleName(a, lang).localeCompare(muscleName(b, lang)),
      ),
    [entries, catalogBySlug, lang],
  );

  return (
    <View style={styles.container}>
      {muscles.length > 0 ? (
        <View style={styles.musclesRow}>
          <Text style={styles.sectionLabel}>
            {t("mobile.preview.muscles", { defaultValue: "Muscles" })}
          </Text>
          <View style={styles.chipWrap}>
            {muscles.map((m) => (
              <View key={m} style={styles.muscleChip}>
                <Text style={styles.muscleChipText}>{muscleName(m, lang)}</Text>
              </View>
            ))}
          </View>
        </View>
      ) : null}

      <PlanIntensityMatrix
        entries={entries}
        catalogBySlug={catalogBySlug}
        exerciseName={resolveName}
        lang={lang}
        unit={unit}
        theme={theme}
        styles={styles}
      />
    </View>
  );
}
